import { createTheme } from '@mui/material/styles';
import './types'; // 型定義の拡張をインポート

// SalonHub Material-UI Theme Configuration
// Material-UI Design System v2.0 (shadcn/ui風 + OKLCH色空間)
const theme = createTheme({
  // Color Palette - Material-UI Design System v2.0 (shadcn/ui風 + OKLCH色空間)
  palette: {
    primary: {
      main: '#6366F1',      // Purple - oklch(0.5106 0.2301 276.9656)
      light: '#A78BFA',     // Purple 400
      dark: '#4338CA',      // Purple 700
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#14B8A6',      // Teal - oklch(0.7038 0.1230 182.5025)
      light: '#2DD4BF',     // Teal 400
      dark: '#0F766E',      // Teal 700
      contrastText: '#ffffff',
    },
    // Accent color (オレンジ) - 重要なCTA用
    accent: {
      main: '#FB923C',      // Orange - oklch(0.7686 0.1647 70.0804)
      light: '#FDBA74',     // Orange 300
      dark: '#EA580C',      // Orange 600
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',   // 白背景
      paper: '#F9FAFB',     // Gray 50 - カード背景
    },
    text: {
      primary: '#111827',   // Gray 900 - メインテキスト
      secondary: '#6B7280', // Gray 500 - セカンダリテキスト
    },
    error: {
      main: '#EF4444',      // Red 500
      light: '#F87171',
      dark: '#DC2626',
    },
    warning: {
      main: '#F59E0B',      // Amber 500
      light: '#FCD34D',
      dark: '#D97706',
    },
    success: {
      main: '#10B981',      // Emerald 500
      light: '#34D399',
      dark: '#059669',
    },
    info: {
      main: '#3B82F6',      // Blue 500
      light: '#60A5FA',
      dark: '#2563EB',
    },
    // Premium Customer用ゴールド（統一: Premium Customer）
    gold: {
      main: '#F59E0B',      // Amber 500 - Premium customers
      light: '#FCD34D',
      dark: '#D97706',
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #FCD34D 50%, #D97706 100%)',
    },
  },

  // Typography
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.25,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.25,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.25,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.25,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.25,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },

  // Shape - shadcn/ui標準
  shape: {
    borderRadius: 8,  // md - Buttons用
  },

  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  // Component Overrides - shadcn/ui風スタイリング
  components: {
    // Button - shadcn/ui風スタイリング
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,        // md (shadcn/ui標準)
          minHeight: '44px',
          minWidth: '44px',
          padding: '12px 24px',
          fontSize: '1rem',
          transition: 'all 0.2s ease-in-out',
        },
        sizeSmall: {
          minHeight: '44px',
          minWidth: '44px',
          padding: '10px 20px',
          fontSize: '0.875rem',
        },
        sizeLarge: {
          minHeight: '48px',
          minWidth: '48px',
          padding: '14px 28px',
          fontSize: '1.125rem',
        },
      },
      variants: [
        {
          props: { color: 'gold' as 'inherit' | 'primary' | 'secondary' },
          style: {
            backgroundColor: '#F59E0B',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#D97706',
            },
          },
        },
        {
          props: { color: 'accent' as any },
          style: {
            backgroundColor: '#FB923C',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#EA580C',
            },
          },
        },
      ],
    },

    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          minHeight: '44px',
          minWidth: '44px',
          padding: '10px',
        },
        sizeSmall: {
          minHeight: '44px',
          minWidth: '44px',
          padding: '8px',
        },
        sizeLarge: {
          minHeight: '48px',
          minWidth: '48px',
          padding: '12px',
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          minHeight: '44px',
          fontSize: '0.875rem',
          padding: '8px 12px',
          borderRadius: 8,  // md
        },
        sizeSmall: {
          minHeight: '44px',
          fontSize: '0.75rem',
          padding: '6px 10px',
        },
        sizeMedium: {
          minHeight: '44px',
          fontSize: '0.875rem',
          padding: '8px 12px',
        },
        clickable: {
          minHeight: '44px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
      variants: [
        {
          props: { color: 'gold' },
          style: {
            backgroundColor: '#F59E0B',
            color: '#ffffff',
          },
        },
      ],
    },

    // Badge
    MuiBadge: {
      variants: [
        {
          props: { color: 'gold' },
          style: {
            '& .MuiBadge-badge': {
              backgroundColor: '#F59E0B',
              color: '#ffffff',
            },
          },
        },
      ],
    },

    // TableCell
    MuiTableCell: {
      styleOverrides: {
        root: {
          minHeight: '48px',
          padding: '12px 16px',
        },
      },
    },

    // Card - shadcn/ui風スタイリング
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,  // lg (shadcn/ui標準 - Cards用)
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5E7EB',  // Gray 200
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },

    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,  // md
        },
      },
    },

    // TextField
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,  // md
          },
        },
      },
    },
  },
});

export default theme;

// Common style utilities
export const commonStyles = {
  // Flex layouts
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  flexStart: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  
  // Spacing
  section: {
    mb: 4,
    p: 3,
  },
  card: {
    p: 3,
    borderRadius: 2,
  },
  
  // Text styles
  sectionTitle: {
    fontWeight: 600,
    mb: 2,
  },
  
  // Responsive helpers
  hiddenOnMobile: {
    display: { xs: 'none', sm: 'block' },
  },
  hiddenOnDesktop: {
    display: { xs: 'block', sm: 'none' },
  },
};

// Breakpoint helpers
export const breakpoints = {
  mobile: (theme: { breakpoints: { down: (breakpoint: string) => string } }) => theme.breakpoints.down('sm'),
  tablet: (theme: { breakpoints: { between: (start: string, end: string) => string } }) => theme.breakpoints.between('sm', 'md'),
  desktop: (theme: { breakpoints: { up: (breakpoint: string) => string } }) => theme.breakpoints.up('md'),
  largeDesktop: (theme: { breakpoints: { up: (breakpoint: string) => string } }) => theme.breakpoints.up('lg'),
};